import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { GitMerge, Search, Loader2, X, ExternalLink } from 'lucide-react';
import api from '../../../services/api';

const ROLE_OPTIONS = [
  { value: 'any', label: 'Parent + Customer (default)' },
  { value: 'parent', label: 'Parent only' },
  { value: 'customer', label: 'Customer only' },
  { value: 'all', label: 'All roles (incl. admin/staff/coach/player)' },
];

const RoleBadge = ({ role }) => {
  if (!role) return null;
  const colors = {
    parent: 'bg-blue-100 text-blue-700',
    customer: 'bg-orange-100 text-orange-700',
    admin: 'bg-purple-100 text-purple-700',
    staff: 'bg-emerald-100 text-emerald-700',
    coach: 'bg-amber-100 text-amber-700',
    player: 'bg-slate-200 text-slate-700',
  };
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide ${colors[role] || 'bg-slate-100 text-slate-600'}`}>
      {role}
    </span>
  );
};

const profileHrefFor = (u) => {
  if (!u) return '#';
  if (u.role === 'customer') return `/admin/customers/${u.id}`;
  if (u.role === 'parent') return `/admin/parents/${u.id}`;
  if (u.role === 'player') return `/admin/players/${u.id}`;
  return `/admin/users/${u.id}`;
};

const UserSearchPicker = ({ slotLabel, role, picked, onPicked, otherPickedId, testIdPrefix }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef();

  // Type for player merge: a separate flow exists; this picker is only used
  // for parent/customer manual merge. We intentionally don't fetch on mount —
  // wait until the admin types ≥2 chars.
  useEffect(() => {
    if (picked) return; // hide search once a record is locked in
    if (!q || q.trim().length < 2) {
      setResults([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get('/admin/duplicates/users/search', {
          params: { q: q.trim(), role, limit: 12 },
        });
        setResults(r.data?.results || []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, role, picked]);

  if (picked) {
    return (
      <div className="flex-1 min-w-[260px]">
        <Label className="text-sm">{slotLabel}</Label>
        <div className="bg-white border-2 border-blue-300 rounded p-2 flex items-start justify-between gap-2"
             data-testid={`${testIdPrefix}-picked`}>
          <div className="text-sm">
            <div className="flex items-center gap-2 flex-wrap">
              <a href={profileHrefFor(picked)} target="_blank" rel="noopener noreferrer"
                 className="font-medium text-blue-700 hover:underline inline-flex items-center gap-1">
                {picked.name || '(no name)'}
                <ExternalLink className="h-3 w-3 opacity-60" />
              </a>
              <RoleBadge role={picked.role} />
              {picked.numeric_id && (
                <span className="text-[10px] text-slate-500 font-mono bg-slate-100 px-1.5 py-0.5 rounded">
                  {picked.numeric_id}
                </span>
              )}
              <span className="text-[10px] text-slate-400 font-mono bg-slate-50 px-1.5 py-0.5 rounded border" title={picked.id}>
                {String(picked.id || '').slice(0, 8)}…
              </span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {picked.email || '—'} · {picked.phone || '—'}
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => onPicked(null)}
            className="text-slate-500 hover:text-rose-600"
            data-testid={`${testIdPrefix}-clear-btn`}
            aria-label="Clear selection"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-w-[260px] relative">
      <Label className="text-sm">{slotLabel}</Label>
      <div className="relative">
        <Input
          placeholder="Search name, email, phone, or PR-ID…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setOpen(true)}
          className="bg-white pr-7"
          data-testid={`${testIdPrefix}-input`}
        />
        {searching && (
          <Loader2 className="absolute right-2 top-2.5 h-4 w-4 animate-spin text-slate-400" />
        )}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow-lg max-h-72 overflow-y-auto"
             data-testid={`${testIdPrefix}-results`}>
          {results.map((u) => {
            const isOther = u.id === otherPickedId;
            return (
              <button
                key={u.id}
                type="button"
                disabled={isOther}
                className={`w-full text-left px-3 py-2 hover:bg-slate-50 border-b last:border-0 text-sm ${isOther ? 'opacity-40 cursor-not-allowed' : ''}`}
                onClick={() => { if (!isOther) { onPicked(u); setOpen(false); setQ(''); } }}
                data-testid={`${testIdPrefix}-result-${u.id}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{u.name || '(no name)'}</span>
                  <RoleBadge role={u.role} />
                  {u.numeric_id && (
                    <span className="text-[10px] text-slate-500 font-mono bg-slate-100 px-1 rounded">
                      {u.numeric_id}
                    </span>
                  )}
                  <span className="text-[10px] text-slate-400 font-mono">{String(u.id).slice(0, 8)}…</span>
                  {isOther && <span className="text-[10px] text-rose-600">already selected on the other side</span>}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {u.email || '—'} · {u.phone || '—'}
                </div>
              </button>
            );
          })}
        </div>
      )}
      {open && q.trim().length >= 2 && !searching && results.length === 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white border rounded p-3 text-xs text-slate-500"
             data-testid={`${testIdPrefix}-empty`}>
          No matches.
        </div>
      )}
    </div>
  );
};

export const ManualMergeCard = ({
  manualMergeType,
  setManualMergeType,
  manualId1,
  setManualId1,
  manualId2,
  setManualId2,
  manualSearching,
  onCompare,
}) => {
  // For parent/customer merges we use the rich name-search picker. For
  // player merges we keep the legacy ID-input flow (separate UI).
  const [pickedSource, setPickedSource] = useState(null);
  const [pickedTarget, setPickedTarget] = useState(null);
  const [roleFilter, setRoleFilter] = useState('any');

  // Sync the picker state into the legacy `manualId1` / `manualId2` so the
  // existing `onCompare` handler in `DuplicateManagement.js` keeps working.
  useEffect(() => {
    if (manualMergeType === 'parent') {
      setManualId1(pickedSource?.id || '');
      setManualId2(pickedTarget?.id || '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedSource, pickedTarget, manualMergeType]);

  const handleCompare = () => {
    if (manualMergeType === 'parent' && pickedSource?.id === pickedTarget?.id) {
      return;
    }
    onCompare();
  };

  const sameId = manualMergeType === 'parent'
    && pickedSource?.id && pickedTarget?.id
    && pickedSource.id === pickedTarget.id;

  return (
    <Card className="border-blue-200 bg-blue-50/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <GitMerge className="h-5 w-5 text-blue-600" />
          Manual Merge
        </CardTitle>
        <CardDescription>
          Search by name, email, phone, or numeric ID — pick two records to merge them.
          Pure-ID input is still supported below for player merges.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-44">
            <Label htmlFor="merge-type" className="text-sm">Record Type</Label>
            <Select value={manualMergeType} onValueChange={(v) => {
              setManualMergeType(v);
              setPickedSource(null);
              setPickedTarget(null);
            }}>
              <SelectTrigger id="merge-type" className="bg-white" data-testid="manual-merge-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="parent">Parent / Customer</SelectItem>
                <SelectItem value="player">Player</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {manualMergeType === 'parent' && (
            <div className="w-64">
              <Label htmlFor="role-filter" className="text-sm">Search Within</Label>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger id="role-filter" className="bg-white" data-testid="manual-merge-role-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {manualMergeType === 'parent' ? (
          <div className="flex flex-wrap items-end gap-3">
            <UserSearchPicker
              slotLabel="First Record"
              role={roleFilter}
              picked={pickedSource}
              onPicked={setPickedSource}
              otherPickedId={pickedTarget?.id}
              testIdPrefix="manual-merge-source"
            />
            <UserSearchPicker
              slotLabel="Second Record"
              role={roleFilter}
              picked={pickedTarget}
              onPicked={setPickedTarget}
              otherPickedId={pickedSource?.id}
              testIdPrefix="manual-merge-target"
            />
            <Button
              onClick={handleCompare}
              disabled={manualSearching || !pickedSource || !pickedTarget || sameId}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
              data-testid="manual-merge-compare-btn"
              title={sameId ? 'Same record on both sides' : undefined}
            >
              {manualSearching ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Searching...</>
              ) : (
                <><Search className="h-4 w-4 mr-2" />Compare &amp; Merge</>
              )}
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <Label htmlFor="id1" className="text-sm">First Player ID</Label>
              <Input
                id="id1" placeholder="e.g., P-1001 or UUID"
                value={manualId1} onChange={(e) => setManualId1(e.target.value)}
                className="bg-white" data-testid="manual-merge-id1"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label htmlFor="id2" className="text-sm">Second Player ID</Label>
              <Input
                id="id2" placeholder="e.g., P-1002 or UUID"
                value={manualId2} onChange={(e) => setManualId2(e.target.value)}
                className="bg-white" data-testid="manual-merge-id2"
              />
            </div>
            <Button
              onClick={onCompare}
              disabled={manualSearching || !manualId1.trim() || !manualId2.trim()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="manual-merge-compare-btn"
            >
              {manualSearching ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Searching...</>
              ) : (
                <><Search className="h-4 w-4 mr-2" />Compare &amp; Merge</>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
